import { DropdownMenu } from '@design/components/dropdown-menu/DropdownMenu';
import {
  DropdownEntry,
  DropdownGroup,
} from '@design/components/dropdown-menu/dropdown-group/DropdownGroup';
import { Icon } from '@design/components/icon/Icon';
import { Spinner } from '@design/components/spinner/Spinner';
import { generatePath } from '@helpers';
import { ActionModal } from '@terminal/components/actions-modal/ActionsModal';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { useTouchDevice } from '@terminal/hooks/MediaQueryHook';
import { appPaths } from '@terminal/routes/app-paths';
import { queryKeys, queryValues } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useChatNavigation } from '@terminal/views/alfred/use-chat-navigation/useChatNavigation';
import clsx from 'clsx';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useMatch } from 'react-router';

import { CategoryType } from '../history-sidebar/HistorySidebar';
import { useReasonerParams } from '../hooks/useReasonerParams';
import { ChatStore, Conversation } from '../use-chat/use-chat.types';
import { useChat } from '../use-chat/useChat';
import styles from './HistoryPill.module.scss';

interface HistoryPillProps {
  conversation: Conversation;
  category: CategoryType;
  handleOpenSharingModal: () => void;
  depth?: number;
}

export const HistoryPill = ({
  conversation,
  category,
  handleOpenSharingModal,
  depth = 0,
}: HistoryPillProps) => {
  const { t } = useTranslation(['chat']);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { updateConversation, getActiveConversationTasks } = useChat(state => ({
    updateConversation: state.updateConversation,
    getActiveConversationTasks: state.getActiveConversationTasks,
  }));
  const { trackAlfredChatBookmark, trackAlfredShare } = usePosthogEvents();
  const isSnbcUser = useFlags(state => state.getFeatureFlagState('snbc_user'));

  const { navigateToConversation } = useChatNavigation();

  const { conversationId, setReasonerParams } = useReasonerParams();
  const isAlfredPath = useMatch(appPaths.alfred);

  const isTouchDevice = useTouchDevice();

  const isProcessing = conversation.status === 'Processing';

  const hasSuccessfulTask = conversation.tasks
    ? conversation.tasks.some(task => task.last_state?.status === 'OK')
    : true;

  const setActiveModal = (props: ChatStore['actionModal']) => {
    useChat.setState({ actionModal: props });
  };

  const isScheduledTaskEnabled = useFlags(state =>
    state.getFeatureFlagState('scheduled_task_feature')
  );
  const isUnread =
    conversation.unRead === true && conversation.status === 'Done';
  const isScheduledTask =
    !!conversation.scheduled_query_id && isScheduledTaskEnabled;

  const handleLeftClick = (e: React.MouseEvent) => {
    if (isDropdownOpen) {
      return;
    }

    if (e.metaKey || e.ctrlKey) {
      e.preventDefault();
      navigateToConversation(conversation.id, { newTab: true });
    } else {
      getActiveConversationTasks(conversation.id);
    }
  };

  const handleRightClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDropdownOpen(true);
  };

  const handleDropdownOpenChange = (open: boolean) => {
    setIsDropdownOpen(open);
  };

  const dropdownItems: DropdownEntry[] = useMemo(() => {
    if (conversation.access_level === 'public') {
      return [
        {
          label: t('chat:historyTab.share'),
          iconName: 'Share' as const,
          key: 'share',
          onClick: () => {
            trackAlfredShare(POSTHOG_TRACKING_SOURCES.ALFRED, 'copy');
            handleOpenSharingModal();
            setReasonerParams({ conversationId: conversation.id });
          },
        },
        {
          label: t('chat:historyTab.openInNewTab'),
          iconName: 'ExternalLink',
          key: 'openInNewTab',
          onClick: () => {
            navigateToConversation(conversation.id, { newTab: true });
          },
        },
        ...(!isTouchDevice && !isSnbcUser
          ? [
              {
                label: t('chat:historyTab.delete'),
                iconName: 'Trash' as const,
                key: 'delete',
                onClick: () =>
                  setActiveModal({
                    type: ActionModal.Delete,
                    id: conversation.id,
                  }),
              },
            ]
          : []),
      ];
    }

    if (conversation.access_level === 'private') {
      return [
        {
          label: conversation.favourite
            ? t('chat:historyTab.removeBookmark')
            : t('chat:historyTab.bookmark'),
          iconName: conversation.favourite ? 'Bookmarked' : 'AddToBookmark',
          key: 'star',
          onClick: () => {
            const type = conversation.favourite ? 'unbookmark' : 'bookmark';
            trackAlfredChatBookmark(POSTHOG_TRACKING_SOURCES.ALFRED, type);
            updateConversation({
              id: conversation.id,
              favourite: !conversation.favourite,
            });
          },
        },
        ...(!isTouchDevice
          ? [
              {
                label: t('chat:historyTab.rename'),
                iconName: 'Pencil' as const,
                key: 'edit',
                onClick: () =>
                  setActiveModal({
                    type: ActionModal.Rename,
                    id: conversation.id,
                  }),
              },
              {
                label: t('chat:historyTab.share'),
                iconName: 'Share' as const,
                key: 'share',
                onClick: () => {
                  handleOpenSharingModal();
                  setReasonerParams({ conversationId: conversation.id });
                },
                disabled: isProcessing || !hasSuccessfulTask,
              },
            ]
          : []),
        {
          label: t('chat:historyTab.openInNewTab'),
          iconName: 'ExternalLink',
          key: 'openInNewTab',
          onClick: () => {
            navigateToConversation(conversation.id, { newTab: true });
          },
        },
        ...(!isTouchDevice && !isSnbcUser
          ? [
              {
                label: t('chat:historyTab.delete'),
                iconName: 'Trash' as const,
                key: 'delete',
                onClick: () =>
                  setActiveModal({
                    type: ActionModal.Delete,
                    id: conversation.id,
                  }),
              },
            ]
          : []),
      ];
    }

    return [];
  }, [
    conversation.access_level,
    conversation.id,
    conversation.favourite,
    isProcessing,
    hasSuccessfulTask,
    isTouchDevice,
    isSnbcUser,
  ]);

  return (
    <>
      <Link
        className={styles.historyPillWrapper}
        to={generatePath({
          basePath: appPaths.alfred,
          params: {
            [queryKeys.conversationId]: conversation.id,
            [queryKeys.mode]: queryValues.deepResearchMode,
          },
        })}
        onClick={handleLeftClick}
        onContextMenu={!isTouchDevice ? handleRightClick : undefined}
      >
        {conversation.favourite && category === 'all' && (
          <Icon
            className={styles.bookmarkIcon}
            iconName="Bookmarked"
            size={20}
            fillColor="var(--icon-disabled)"
          />
        )}
        <TooltipContainer
          label={
            <div className={styles.tooltipLabel}>
              <span className={styles.tooltipTitle}>{conversation.name}</span>
            </div>
          }
          placement="left"
          offsetX={25}
          inPortal
          disabled={isTouchDevice}
        >
          <div
            className={clsx(styles.labelWrapper, {
              [styles.isActive]:
                isAlfredPath && conversationId === conversation.id,
            })}
          >
            {!!depth && (
              <div className={styles.stroke}>
                <div className={styles.verticalLine} />
                <div className={styles.horizontalLine} />
              </div>
            )}
            <div
              className={clsx(styles.label, {
                [styles.labelRootDepth]: depth === 0,
              })}
            >
              {!!depth && <Icon iconName="Share" size={16} />}
              {isScheduledTask && (
                <Icon
                  iconName="Clock"
                  size={16}
                  fillColor="var(--icon-default)"
                />
              )}
              {isUnread && (
                <Icon
                  iconName="CheckmarkCircle"
                  size={12}
                  fillColor="var(--icon-success)"
                />
              )}
              {isProcessing && <Spinner size="extraSmall" useAccentColor />}
              <span>{conversation.name || conversation.summary}</span>
            </div>
            <DropdownMenu
              className={styles.styledDropdownMenu}
              triggerNode={<Icon iconName="Ellipsis" size={16} />}
              triggerProps={{
                onPointerDown: (e: React.PointerEvent) => {
                  if (e.ctrlKey || e.metaKey) {
                    e.preventDefault();
                  }
                },
              }}
              contentProps={{
                align: 'end',
                side: 'bottom',
                onClick: (e: React.MouseEvent) => e.stopPropagation(),
              }}
              open={isDropdownOpen}
              onOpenChange={handleDropdownOpenChange}
            >
              <DropdownGroup items={dropdownItems} />
            </DropdownMenu>
          </div>
        </TooltipContainer>
      </Link>
      {category === 'all' &&
        conversation.public_copies?.map(copy => (
          <HistoryPill
            key={copy.id}
            conversation={copy}
            handleOpenSharingModal={handleOpenSharingModal}
            depth={depth + 1}
            category={category}
          />
        ))}
    </>
  );
};
